/**
 * AudioWaveform Component
 *
 * Real-time audio frequency visualizer using Web Audio API.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './AudioWaveform.css';

const AudioWaveform = ({
  audioLevel = 0, // Fallback if no mediaStream
  isActive = false,
  variant = 'bars', // 'bars' | 'wave' | 'circular'
  barCount = 16,
  color = '#2ecc71'
}) => {
  const [levels, setLevels] = useState(new Array(barCount).fill(0.1));

  // Simulate waveform based on audioLevel when no real data
  useEffect(() => {
    if (!isActive) {
      setLevels(new Array(barCount).fill(0.1));
      return;
    }

    // Create animated wave effect based on audioLevel
    const interval = setInterval(() => {
      setLevels(prevLevels =>
        prevLevels.map((_, i) => {
          const base = audioLevel * 0.7;
          const wave = Math.sin(Date.now() / 150 + i * 0.5) * 0.3;
          const randomness = Math.random() * 0.2;
          return Math.max(0.1, Math.min(1, base + wave + randomness));
        })
      );
    }, 50);

    return () => clearInterval(interval);
  }, [isActive, audioLevel, barCount]);

  // Render bars variant
  const renderBars = () => (
    <div className="waveform-bars">
      {levels.map((level, i) => (
        <motion.div
          key={i}
          className="waveform-bar"
          initial={{ scaleY: 0.1 }}
          animate={{
            scaleY: level,
            backgroundColor: `hsl(${140 + level * 40}, 70%, ${45 + level * 20}%)`
          }}
          transition={{ duration: 0.05, ease: 'easeOut' }}
          style={{
            transformOrigin: 'bottom'
          }}
        />
      ))}
    </div>
  );

  // Render wave variant (SVG path)
  const renderWave = () => {
    const width = 200;
    const height = 40;
    const points = levels.map((level, i) => {
      const x = (i / (levels.length - 1)) * width;
      const y = height / 2 - (level * height * 0.4);
      return `${x},${y}`;
    });
    const pathD = `M 0,${height / 2} ${points.map((p, i) => `L ${p}`).join(' ')} L ${width},${height / 2}`;

    return (
      <svg className="waveform-wave" viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2ecc71" />
            <stop offset="50%" stopColor="#3498db" />
            <stop offset="100%" stopColor="#2ecc71" />
          </linearGradient>
        </defs>
        <motion.path
          d={pathD}
          fill="none"
          stroke="url(#waveGradient)"
          strokeWidth="3"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
        />
        {/* Mirror wave below */}
        <motion.path
          d={pathD}
          fill="none"
          stroke="url(#waveGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          opacity={0.3}
          style={{ transform: 'scaleY(-1)', transformOrigin: 'center' }}
        />
      </svg>
    );
  };

  // Render circular variant
  const renderCircular = () => {
    const centerX = 60;
    const centerY = 60;
    const baseRadius = 40;

    return (
      <svg className="waveform-circular" viewBox="0 0 120 120">
        <defs>
          <radialGradient id="circularGradient">
            <stop offset="0%" stopColor="#2ecc71" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#3498db" stopOpacity="0.2" />
          </radialGradient>
        </defs>
        {levels.map((level, i) => {
          const angle = (i / levels.length) * Math.PI * 2 - Math.PI / 2;
          const barLength = 8 + level * 15;
          const x1 = centerX + Math.cos(angle) * baseRadius;
          const y1 = centerY + Math.sin(angle) * baseRadius;
          const x2 = centerX + Math.cos(angle) * (baseRadius + barLength);
          const y2 = centerY + Math.sin(angle) * (baseRadius + barLength);

          return (
            <motion.line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={`hsl(${140 + level * 40}, 70%, 50%)`}
              strokeWidth="3"
              strokeLinecap="round"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 + level * 0.4 }}
              transition={{ duration: 0.05 }}
            />
          );
        })}
      </svg>
    );
  };

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          className={`audio-waveform ${variant}`}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          {variant === 'bars' && renderBars()}
          {variant === 'wave' && renderWave()}
          {variant === 'circular' && renderCircular()}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AudioWaveform;
